import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import { HTMLProps, ReactNode, forwardRef } from 'react';

import { cn } from '@/lib/css-utils';

import { BaseUIComponentProps } from '../base';
import { Paragraph } from '../paragraph';

export type InputVariant = 'outline' | 'filled';

const inputWrapperVariants = cva(
    classNames(
        'p-2 rounded-lg border has-[input:disabled]:cursor-not-allowed',
        'has-[input:focus-within]:outline-none group',
        'flex items-center justify-start gap-2'
    ),
    {
        variants: {
            variant: {
                outline: 'bg-transparent',
                filled: 'bg-neutral-900 ',
            },
            hasError: {
                true: 'border-nError-500 text-nError-500 has-[input:disabled]:opacity-50',
                false: classNames(
                    'border-neutral-600 hover:border-neutral-400 has-[input:focus-within]:border-neutral-400',
                    'has-[input:disabled]:border-neutral-600 has-[input:disabled]:hover:border-neutral-600 has-[input:disabled]:has-[input:focus-within]:border-neutral-600',

                    'text-neutral-200',
                    'has-[input:disabled]:text-neutral-500 has-[input:disabled]:hover:text-neutral-500',
                    'hover:text-neutral-50 has-[input:focus-within]:text-neutral-50'
                ),
            },
        },
        defaultVariants: {
            variant: 'outline',
            hasError: false,
        },
    }
);

const inputVariants = cva(
    'focus:outline-none text-xs leading-4.5 font-roboto bg-transparent p-0 grow disabled:cursor-not-allowed',
    {
        variants: {
            hasError: {
                true: 'text-nError-500 placeholder-nError-500',
            },
            mode: {
                light:
                    'text-neutral-700 focus:text-neutral-900 group-hover:text-neutral-900 disabled:text-neutral-500 disabled:group-hover:text-neutral-500 ' +
                    'placeholder-neutral-200 group-hover:placeholder-neutral-200 disabled:placeholder-neutral-500 disabled:group-hover:placeholder-neutral-500',

                dark:
                    'text-neutral-200 focus:text-neutral-50 group-hover:text-neutral-50 disabled:text-neutral-500 disabled:group-hover:text-neutral-500 ' +
                    'placeholder-neutral-200 group-hover:placeholder-neutral-50 disabled:placeholder-neutral-500 disabled:group-hover:placeholder-neutral-500',
            },
        },
        defaultVariants: {
            hasError: false,
            mode: 'dark',
        },
    }
);

export type InputProps = {
    hasError?: boolean;
    disabled?: boolean;

    variant?: InputVariant;

    inputClassName?: string;

    prefix?: ReactNode;
    suffix?: ReactNode;

    inputRef?: React.Ref<HTMLInputElement>;
} & BaseUIComponentProps &
    Omit<HTMLProps<HTMLInputElement>, 'prefix' | 'suffix'>;

export const Input = forwardRef<HTMLDivElement, InputProps>(
    (
        {
            hasError = false,
            disabled = false,
            className,
            inputClassName,
            variant,
            inputRef,
            prefix,
            suffix,
            mode,
            ...props
        },
        ref
    ) => {
        return (
            <div className={cn(inputWrapperVariants({ hasError, variant, className }))} ref={ref}>
                {prefix ? <div className="node-prefix">{prefix}</div> : null}

                <input
                    className={cn(
                        inputVariants({ hasError, className: inputClassName, mode }),
                        'input-hide-spin' // 👈 this hides the spinner UI
                    )}
                    disabled={disabled}
                    ref={inputRef}
                    {...props}
                />

                {suffix ? (
                    <Paragraph size="xxs" className="text-neutral-400">
                        {suffix}
                    </Paragraph>
                ) : null}
            </div>
        );
    }
);

Input.displayName = 'Input';
